import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Upload, FileSpreadsheet, Trash2, Loader2, CheckCircle2 } from "lucide-react";
import { fmt, fmtDate } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function ImportPage() {
  const [imports, setImports] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const load = useCallback(() => {
    api.get("/imports").then((r) => setImports(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setProgress(null);
    setLastResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/import/excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.info("Soubor nahrán, zpracovávám na pozadí…");
      pollImport(r.data.job_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nahrání se nezdařilo");
      setUploading(false);
    } finally {
      e.target.value = "";
    }
  };

  const pollImport = (jobId) => {
    const tick = async () => {
      try {
        const s = await api.get(`/import/excel/status/${jobId}`);
        const d = s.data;
        setProgress(d);
        if (d.status === "done") {
          setLastResult({ import: { rows: d.total }, materials: d.materials });
          toast.success(`Import dokončen: ${d.total} řádků, ${d.materials} surovin`);
          setUploading(false);
          setProgress(null);
          load();
          return;
        }
        if (d.status === "error") {
          toast.error(`Import se nezdařil: ${d.error || "neznámá chyba"}`);
          setUploading(false);
          setProgress(null);
          return;
        }
        setTimeout(tick, 2000);
      } catch (err) {
        toast.error("Nepodařilo se načíst stav importu");
        setUploading(false);
        setProgress(null);
      }
    };
    setTimeout(tick, 1500);
  };

  const del = async (id) => {
    await api.delete(`/imports/${id}`);
    toast.success("Import smazán");
    load();
  };

  return (
    <div className="fade-up">
      <PageHeader title="Import dat" subtitle="Nahrajte Excel s nakupovanými surovinami (Položky nákupu)" />
      <div className="px-10 py-8 space-y-8 max-w-4xl">
        <label
          data-testid="import-dropzone"
          className="block border-2 border-dashed border-border rounded-xl bg-white p-12 text-center cursor-pointer hover:border-primary transition-colors">
          <input type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile} disabled={uploading}
            data-testid="import-file-input" />
          {uploading ? (
            <Loader2 className="h-10 w-10 mx-auto text-primary animate-spin" />
          ) : (
            <Upload className="h-10 w-10 mx-auto text-primary" strokeWidth={1.5} />
          )}
          <div className="mt-4 font-medium">{uploading ? "Zpracovávám soubor…" : "Klikněte pro výběr Excel souboru"}</div>
          {uploading && progress && progress.total > 0 && (
            <div className="text-sm text-primary mt-1" data-testid="import-progress">
              Uloženo {fmt(progress.inserted, 0)} z {fmt(progress.total, 0)} řádků…
            </div>
          )}
          <div className="text-sm text-muted-foreground mt-1">
            Očekávané sloupce: Zboží, Název, Druh, Firma, MJ, Množství, Netto fa./MJ, Brutto fa./MJ, WeekNo, Příjemka
          </div>
        </label>

        {lastResult && (
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-5 flex items-center gap-3" data-testid="import-result">
            <CheckCircle2 className="h-5 w-5 text-primary" />
            <div className="text-sm">
              Naimportováno <b>{lastResult.import.rows}</b> řádků. Číselník nyní obsahuje <b>{lastResult.materials}</b> surovin.
            </div>
          </div>
        )}

        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-border font-display text-lg font-medium">Historie importů</div>
          {imports.length === 0 && (
            <div className="px-6 py-10 text-center text-muted-foreground text-sm">Zatím žádné importy.</div>
          )}
          {imports.map((im) => (
            <div key={im.id} data-testid={`import-row-${im.id}`}
              className="px-6 py-4 border-b border-border last:border-0 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="h-5 w-5 text-primary" />
                <div>
                  <div className="font-medium">{im.filename}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(im.created_at)} · {fmt(im.rows, 0)} řádků</div>
                </div>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid={`import-delete-${im.id}`}
                    className="text-muted-foreground hover:text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Smazat import?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Odstraní se všechny řádky z tohoto importu. Kalkulace zůstanou zachovány.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Zrušit</AlertDialogCancel>
                    <AlertDialogAction onClick={() => del(im.id)} data-testid={`import-confirm-delete-${im.id}`}>
                      Smazat
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
